import { type RefObject, useEffect, useLayoutEffect, useRef } from "react";

function fitToContent(element: HTMLTextAreaElement): void {
  element.style.height = "auto";
  // scrollHeight excludes the border; with border-box sizing the height must include it.
  const border = element.offsetHeight - element.clientHeight;
  element.style.height = `${element.scrollHeight + border}px`;
}

/**
 * CR-171: grows a textarea to fit its content, with no height cap.
 *
 * Measures after every render but only resizes when the value or the element
 * itself changed — so a textarea that mounts late (behind a loading spinner)
 * or is remounted (a reset key) is sized for the value it mounts with, without
 * forcing a layout pass on renders that change neither. The `rows` attribute
 * stays the minimum: `height: auto` falls back to it before measuring.
 *
 * A width change (window or sidebar resize) re-wraps the text without a
 * render, so a ResizeObserver re-fits the element when its width changes. Its
 * own height changes are ignored, which keeps the observer from looping.
 */
export function useAutoGrowTextarea(
  ref: RefObject<HTMLTextAreaElement | null>,
  value: string,
): void {
  const last = useRef<{ element: HTMLTextAreaElement | null; value: string | null }>({
    element: null,
    value: null,
  });
  const observed = useRef<{ element: HTMLTextAreaElement; observer: ResizeObserver } | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (element !== observed.current?.element) {
      observed.current?.observer.disconnect();
      observed.current = null;
      if (element && typeof ResizeObserver !== "undefined") {
        let width = element.clientWidth;
        const observer = new ResizeObserver(() => {
          if (element.clientWidth === width) {
            return;
          }
          width = element.clientWidth;
          fitToContent(element);
        });
        observer.observe(element);
        observed.current = { element, observer };
      }
    }

    if (!element || (element === last.current.element && value === last.current.value)) {
      return;
    }
    last.current = { element, value };
    fitToContent(element);
  });

  useEffect(
    () => () => {
      observed.current?.observer.disconnect();
      observed.current = null;
    },
    [],
  );
}
